import { getLoggerManager } from '@/shared/logging'
import type {
  RenderInvalidationService as IRenderInvalidationService,
  RenderActivityContext,
  RenderActivityReport,
  RenderActivitySource,
  RenderInvalidationDiagnostics,
  RenderInvalidationEntry,
  RenderInvalidationOptions,
  RenderInvalidationReason,
} from '@/shared/types/services'

const log = getLoggerManager().getLogger('runtime:invalidation')

/** 全局按需渲染 dirty 状态与 activity 聚合服务。 */
export class RenderInvalidationService implements IRenderInvalidationService {
  private dirty = false
  private frameScheduled = false
  private disposed = false
  private frameRequestCallback: (() => void) | null = null
  private pendingReasons: RenderInvalidationEntry[] = []
  private readonly activitySources = new Map<string, RenderActivitySource>()
  private activeActivities: Array<{
    sourceId: string
    reasons: readonly RenderInvalidationReason[]
    viewIds?: readonly string[] | null
  }> = []

  setFrameRequestCallback(callback: (() => void) | null): void {
    this.frameRequestCallback = callback
    if (callback && this.dirty && !this.frameScheduled) {
      callback()
    }
  }

  invalidate(reason: RenderInvalidationReason, options?: RenderInvalidationOptions): void {
    if (this.disposed) return
    this.dirty = true
    this.pendingReasons.push({
      reason,
      viewId: options?.viewId,
      detail: options?.detail,
      timestamp: performance.now(),
    })
    this.requestFrameIfNeeded()
  }

  requestNextFrame(reason: RenderInvalidationReason, options?: RenderInvalidationOptions): void {
    this.invalidate(reason, options)
  }

  consumeDirty(): readonly RenderInvalidationEntry[] {
    const consumed = this.pendingReasons
    this.pendingReasons = []
    this.dirty = false
    return consumed
  }

  isDirty(): boolean {
    return this.dirty
  }

  setFrameScheduled(scheduled: boolean): void {
    this.frameScheduled = scheduled
  }

  isFrameScheduled(): boolean {
    return this.frameScheduled
  }

  registerActivitySource(source: RenderActivitySource) {
    if (this.disposed) {
      return { dispose: () => {} }
    }

    const existing = this.activitySources.get(source.id)
    if (existing && existing !== source) {
      log.warn(`替换已存在的渲染活动源: ${source.id}`)
    }
    this.activitySources.set(source.id, source)

    return {
      dispose: () => {
        if (this.activitySources.get(source.id) === source) {
          this.activitySources.delete(source.id)
        }
      },
    }
  }

  collectActivity(context: RenderActivityContext): RenderActivityReport {
    const reasons: RenderInvalidationReason[] = []
    const viewIds = new Set<string>()
    let hasGlobalActivity = false
    const activeActivities: Array<{
      sourceId: string
      reasons: readonly RenderInvalidationReason[]
      viewIds?: readonly string[] | null
    }> = []

    for (const source of Array.from(this.activitySources.values())) {
      try {
        const report = source.getRenderActivity(context)
        if (!report?.needsNextFrame) {
          continue
        }
        const sourceReasons = report.reasons?.length ? report.reasons : [source.id]
        reasons.push(...sourceReasons)
        if (report.viewIds?.length) {
          for (const viewId of report.viewIds) {
            viewIds.add(viewId)
          }
        } else {
          hasGlobalActivity = true
        }
        activeActivities.push({
          sourceId: source.id,
          reasons: sourceReasons,
          viewIds: report.viewIds,
        })
      } catch (err) {
        log.warn(
          `渲染活动源查询失败: ${source.id}, reason=${err instanceof Error ? err.message : String(err)}`
        )
      }
    }

    this.activeActivities = activeActivities
    return {
      needsNextFrame: reasons.length > 0,
      reasons,
      ...(!hasGlobalActivity && viewIds.size > 0 ? { viewIds: Array.from(viewIds) } : {}),
    }
  }

  getDiagnostics(): RenderInvalidationDiagnostics {
    return {
      dirty: this.dirty,
      frameScheduled: this.frameScheduled,
      pendingReasons: [...this.pendingReasons],
      activeActivities: this.activeActivities.map((activity) => ({
        sourceId: activity.sourceId,
        reasons: [...activity.reasons],
        viewIds: activity.viewIds ? [...activity.viewIds] : activity.viewIds,
      })),
    }
  }

  dispose(): void {
    this.disposed = true
    this.dirty = false
    this.frameScheduled = false
    this.frameRequestCallback = null
    this.pendingReasons = []
    this.activitySources.clear()
    this.activeActivities = []
  }

  private requestFrameIfNeeded(): void {
    if (this.frameScheduled) return
    this.frameRequestCallback?.()
  }
}
